import OpenInNew from "@mui/icons-material/OpenInNew";
import { Link } from "@mui/material";

const RCRA_PUBLIC_SEARCH_BASE = "https://rcrapublic.epa.gov/rcra-hwip";

interface RcraInfoLinkProps {
  /**
   * When set, deep-links to that site's record in the RCRA Hazardous Waste
   * Information Portal; when omitted, opens the public search home so the user
   * can look a site up by name/state (e.g. to find a correct EPA ID).
   */
  epaId?: string | null;
  /**
   * Link text. Defaults to "View in RCRAInfo HWIP" when deep-linking a specific
   * `epaId`, or "Search RCRAInfo HWIP" when opening the public search home.
   */
  label?: string;
}

/**
 * External link to the EPA's public RCRA Hazardous Waste Information Portal,
 * opened in a new tab. Reviewers use it to confirm or look up an EPA ID.
 */
export function RcraInfoLink({ epaId, label }: RcraInfoLinkProps) {
  const trimmedId = epaId?.trim();
  // Deep-link to a site *search* for the id rather than the site-detail page:
  // an unrecognized id then shows a graceful "no site found" results page
  // instead of the detail view's error banner.
  const href = trimmedId
    ? `${RCRA_PUBLIC_SEARCH_BASE}/search/results?type=site&criteria=${encodeURIComponent(
        trimmedId
      )}&basic=true`
    : `${RCRA_PUBLIC_SEARCH_BASE}/`;
  const text = label ?? (trimmedId ? "View in RCRAInfo HWIP" : "Search RCRAInfo HWIP");

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      {text}
      <OpenInNew fontSize="inherit" />
    </Link>
  );
}
